/**
 * form renderer（EP-2・M7b）。誌面に入力フォームを置く。
 *
 * 2 形態（サーバ `Overlay\OverlayInput` と対）:
 *   自前型  … payload に `form_id` と（配信 API がサーバ側で埋めた）`definition` を持つ。
 *             定義からフィールドを描き、`POST /forms/{id}/submissions` へ送る。
 *   外部型  … payload に `url` を持つ。**クリックで初めて iframe をマウント**し、以後保持する
 *             （めくって戻っても入力が消えない）。送信は外部サービスの管轄。
 *
 * ゲート（EP-5 `gate`）の通過条件になっているフォームは、送信成功の応答 `unlocked` を見て
 * {@link OverlayContext.unlocked} を呼ぶ（案内のうえ再読み込みして許可範囲を取り直す）。
 *
 * payload の `display: 'popup'`（T012・D178）では誌面にマーカーだけを置き、クリックで開く
 * ポップアップの中に同じフォームを描く（{@link formPopupRenderer}）。
 */
import { cls, uniqueId } from '../../brand';
import type { Translate } from '../../i18n/i18n';
import type { OverlayContext, OverlayItem, OverlayRenderer } from '../registry';
import { markerPopupRenderer } from './popup';

/** 入力欄の要素（1 フィールドが持ちうる器）。 */
type Control = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;

/** 1 フィールド分の描画結果（誤りの提示と送信に要る参照をまとめて持つ）。 */
interface FieldParts {
  field: FormField;
  /** 誤りのときに焦点を当てる先（ラジオ群は先頭の選択肢）。 */
  control: Control;
  /** 誤りの印（`aria-invalid`・`aria-describedby`）を付ける先。ラジオ群は選択肢すべて。 */
  marks: Control[];
  /** その欄の誤りを書く場所（既定は空・非表示）。 */
  error: HTMLElement;
  /** 送信値。checkbox は真偽・ラジオは選ばれた値・他は入力値。 */
  value: () => string | boolean;
}

interface FormField {
  name: string;
  label: string;
  type: string;
  required?: boolean;
  /** 選択肢（`select` と `radio` が使う）。 */
  options?: string[];
  /** 入力目的（WCAG 2.1 の 1.3.5）。省略時は type から既定を出す。 */
  autocomplete?: string;
}

/**
 * 型から出す `autocomplete` の既定（WCAG 2.1 の 1.3.5）。
 * 利用者本人の情報を集める型だけを持つ — text は「何を集める欄か」が型から
 * 決まらないため既定を出さず、要るなら定義側で明示してもらう。
 */
const AUTOCOMPLETE_BY_TYPE: Readonly<Record<string, string>> = {
  email: 'email',
  tel: 'tel',
};

interface FormDefinition {
  submitLabel?: string;
  successMessage?: string;
  fields?: FormField[];
}

interface FormPayload {
  form_id?: number;
  url?: string;
  label?: string;
  definition?: FormDefinition;
}

export const formRenderer: OverlayRenderer = (el, item, context) => {
  const payload = item.payload as FormPayload;
  if (typeof payload.url === 'string') {
    buildExternal(el, context, payload.url, payload.label);
    return;
  }
  if (typeof payload.form_id === 'number' && payload.definition) {
    buildNative(el, context, item, payload.form_id, payload.definition, payload.label);
  }
  // definition が無い自前型は描かない（サーバが配信段で落とすので通常は来ない）
};

/**
 * 外部フォームの iframe に与える権限（NFR-SEC-01）。
 * フォームが動く最小限 — 送信（forms）・スクリプト・自ドメインの Cookie/セッション
 * （same-origin）・送信後の別タブ遷移（popups）。トップレベル遷移は与えない。
 */
const FORM_SANDBOX = 'allow-forms allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox';

/**
 * ポップアップで出すフォーム。初めて開いたときに、埋め込みと同じ描き方でパネルの中へ
 * 1 度だけ描く — 以後は開閉だけなので、閉じて開き直しても入力した値が残る。外部型は
 * マーカーのクリックが活性化の操作を兼ねるので、ポスターを挟まず iframe を直接置く。
 */
const formPopup = markerPopupRenderer({
  markerClass: 'overlay-form-note',
  labelKey: 'overlay.form.open',
  panelClass: 'overlay-note-panel--form',
  fill: (stage, item, context) => {
    const payload = item.payload as FormPayload;
    if (typeof payload.url === 'string') {
      stage.appendChild(createFormFrame(context.doc, payload.url, payload.label));
    } else if (typeof payload.form_id === 'number' && payload.definition) {
      buildNative(stage, context, item, payload.form_id, payload.definition, payload.label);
    }
  },
});

/** `display: 'popup'` の描き手。中身の描けない自前型（definition 無し）は埋め込みと同じく何も置かない。 */
export const formPopupRenderer: OverlayRenderer = (el, item, context) => {
  const payload = item.payload as FormPayload;
  const drawable =
    typeof payload.url === 'string' || (typeof payload.form_id === 'number' && payload.definition !== undefined);
  return drawable ? formPopup(el, item, context) : undefined;
};

/** 外部フォームの iframe（埋め込みとポップアップで共通）。 */
function createFormFrame(doc: Document, url: string, label?: string): HTMLIFrameElement {
  const frame = doc.createElement('iframe');
  frame.className = cls('overlay-frame');
  frame.setAttribute('sandbox', FORM_SANDBOX);
  frame.src = url;
  if (label !== undefined) frame.title = label;
  return frame;
}

/** 外部フォーム: ポスターのクリックで iframe をマウントし、以後保持する。 */
function buildExternal(el: HTMLElement, context: OverlayContext, url: string, label?: string): void {
  const doc = context.doc;
  const poster = doc.createElement('button');
  poster.type = 'button';
  poster.className = cls('overlay-poster');
  poster.setAttribute('aria-label', label ?? context.translate('overlay.form.open'));
  const icon = doc.createElement('span');
  icon.className = cls('overlay-poster-icon');
  icon.setAttribute('aria-hidden', 'true');
  poster.appendChild(icon);

  poster.addEventListener('click', () => {
    poster.replaceWith(createFormFrame(doc, url, label));
  });
  el.appendChild(poster);
}

/** 自前型フォーム: 定義からフィールドを描き、送信する。 */
function buildNative(
  el: HTMLElement,
  context: OverlayContext,
  item: OverlayItem,
  formId: number,
  definition: FormDefinition,
  label?: string,
): void {
  const doc = context.doc;
  const form = doc.createElement('form');
  form.className = cls('overlay-form');
  // 見出し（オーバーレイの label）。何のためのフォームかを入力欄の前に置き、
  // フォーム自身の名前にもする（読み上げは入力欄へ入る前にこの一行を読む）。
  if (label !== undefined && label !== '') {
    const title = doc.createElement('p');
    title.className = cls('overlay-form-title');
    title.id = uniqueId('overlay-form-title');
    title.textContent = label;
    form.appendChild(title);
    form.setAttribute('aria-labelledby', title.id);
  }
  // 検証は自前で行う（WCAG 3.3.1・3.3.3）。ブラウザ既定に委ねると submit 自体が
  // 起きず、どの項目がなぜ駄目かをこちらから示せない（吹き出しは読み上げも消え方も
  // 実装依存で、拡大表示では画面外へ出ることもある）。
  form.noValidate = true;

  const fields = Array.isArray(definition.fields) ? definition.fields : [];
  const parts: FieldParts[] = [];
  for (const field of fields) {
    const part = appendField(doc, form, field, context.translate);
    if (part) parts.push(part);
  }

  const status = doc.createElement('p');
  status.className = cls('overlay-form-status');
  status.setAttribute('role', 'status');
  status.hidden = true;

  const submit = doc.createElement('button');
  submit.type = 'submit';
  submit.className = `${cls('button')} ${cls('overlay-form-submit')}`;
  submit.textContent = definition.submitLabel ?? context.translate('overlay.form.submit');
  form.appendChild(submit);
  form.appendChild(status);

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    if (context.apiBase === null) return;
    const wrong = validate(parts, context.translate);
    const first = wrong[0];
    if (first !== undefined) {
      status.hidden = false;
      status.textContent = context.translate('overlay.form.errorSummary', { count: wrong.length });
      // 直す場所へ連れて行く（最初の誤り）。名前・説明とも読み上げに載る。
      first.control.focus();
      return;
    }
    void send(context, item, formId, definition, parts, form, submit, status);
  });
  el.appendChild(form);
}

/**
 * 入力の誤りを見つけ、**その欄の隣に何をどう直すかを書く**（WCAG 3.3.1・3.3.3）。
 * 誤りのある欄は `aria-invalid` と `aria-describedby` で説明と結び、直ると消す。
 * 戻り値は誤りのあった欄（画面上の順）。
 */
function validate(parts: readonly FieldParts[], translate: Translate): FieldParts[] {
  const wrong: FieldParts[] = [];
  for (const part of parts) {
    const message = messageFor(part, translate);
    if (message === null) {
      part.error.textContent = '';
      part.error.hidden = true;
      for (const mark of part.marks) {
        mark.removeAttribute('aria-invalid');
        mark.removeAttribute('aria-describedby');
      }
      continue;
    }
    part.error.textContent = message;
    part.error.hidden = false;
    for (const mark of part.marks) {
      mark.setAttribute('aria-invalid', 'true');
      mark.setAttribute('aria-describedby', part.error.id);
    }
    wrong.push(part);
  }
  return wrong;
}

/** 選ぶ形の欄（誤りの文面が「入力してください」ではなく「選択してください」になる）。 */
function isChoice(field: FormField): boolean {
  return field.type === 'select' || field.type === 'radio' || field.type === 'checkbox';
}

/** その欄の誤り（無ければ null）。文面は「何を」「どう直すか」を含む。 */
function messageFor(part: FieldParts, translate: Translate): string | null {
  const { field, control } = part;
  const label = field.label;
  const value = part.value();

  if (field.required === true && (value === false || value === '')) {
    // 直し方は器に合わせて言う（入力する／選ぶ／チェックを入れる）。
    // ラベルが一文のチェックボックスで「…するを選択してください」にならないよう分ける。
    const key =
      field.type === 'checkbox'
        ? 'overlay.form.errorRequiredCheckbox'
        : isChoice(field)
          ? 'overlay.form.errorRequiredCheck'
          : 'overlay.form.errorRequired';
    return translate(key, { label });
  }

  // 空欄の任意項目は形式を問わない（入力していない＝誤りではない）。
  if (typeof value !== 'string' || value.trim() === '') return null;
  // 形式は要素自身の判定を使う（type=email 等の既定規則をそのまま活かす）。
  if (control.checkValidity()) return null;
  return translate(field.type === 'email' ? 'overlay.form.errorEmail' : 'overlay.form.errorFormat', {
    label,
  });
}

/**
 * ラベルをその器へ書く。必須は**目で見て分かる形**でも示す（WCAG 3.3.2）。
 * 記号だけにしないのは、「*」の意味を凡例で説明しないと伝わらないため。読み上げにもそのまま載る。
 * 器は単一の欄なら `<span>`、ラジオ群なら `<legend>`。
 */
function fillCaption(doc: Document, holder: HTMLElement, field: FormField, translate: Translate): void {
  holder.className = cls('overlay-form-label');
  holder.textContent = field.label;
  if (field.required === true) {
    const mark = doc.createElement('span');
    mark.className = cls('overlay-form-required');
    mark.textContent = translate('overlay.form.required');
    holder.appendChild(mark);
  }
}

/** 誤りの説明を書く場所。空のうちは隠しておき、`aria-describedby` で欄と結ぶ。 */
function createError(doc: Document): HTMLElement {
  const error = doc.createElement('span');
  error.className = cls('overlay-form-error');
  error.id = uniqueId('overlay-form-error');
  error.hidden = true;
  return error;
}

/** 1 フィールド分の入力要素を描く（ラベルつき）。 */
function appendField(
  doc: Document,
  form: HTMLFormElement,
  field: FormField,
  translate: Translate,
): FieldParts | null {
  if (field.type === 'radio') return appendRadioGroup(doc, form, field, translate);

  const wrap = doc.createElement('label');
  wrap.className = cls('overlay-form-field');
  const caption = doc.createElement('span');
  fillCaption(doc, caption, field, translate);

  let control: Control;
  let checkbox: HTMLInputElement | null = null;
  if (field.type === 'textarea') {
    control = doc.createElement('textarea');
    wrap.classList.add(cls('overlay-form-field--wide')); // 複数行は列をまたいで置く
  } else if (field.type === 'select') {
    const select = doc.createElement('select');
    for (const option of field.options ?? []) {
      const opt = doc.createElement('option');
      opt.value = option;
      opt.textContent = option;
      select.appendChild(opt);
    }
    control = select;
  } else if (field.type === 'checkbox') {
    checkbox = doc.createElement('input');
    checkbox.type = 'checkbox';
    control = checkbox;
    wrap.classList.add(cls('overlay-form-field--check'));
  } else {
    control = doc.createElement('input');
    control.type = field.type === 'email' || field.type === 'tel' ? field.type : 'text';
  }
  control.name = field.name;
  if (field.required === true) control.required = true;
  // 入力目的をプログラムから分かる形で示す（WCAG 2.1 の 1.3.5）。定義側の明示が
  // あればそれを優先し、無ければ型から出す。`off` は「本人の情報ではない」ことを
  // 導入者が示す手段なので、そのまま属性に載せる（自動入力を止める既定の意味になる）。
  const autocomplete = field.autocomplete ?? AUTOCOMPLETE_BY_TYPE[field.type];
  if (autocomplete !== undefined) control.setAttribute('autocomplete', autocomplete);
  // チェックボックスだけは四角を文言の前に置く（同意文のような長い一文でも、
  // 何にチェックするのかが先に来る）。他の欄はラベル → 入力欄の順のまま。
  if (checkbox !== null) {
    wrap.appendChild(checkbox);
    wrap.appendChild(caption);
  } else {
    wrap.appendChild(caption);
    wrap.appendChild(control);
  }

  const error = createError(doc);
  wrap.appendChild(error);

  form.appendChild(wrap);
  const check = checkbox;
  return {
    field,
    control,
    marks: [control],
    error,
    value: () => (check !== null ? check.checked : control.value),
  };
}

/**
 * ラジオ群を描く（`fieldset` ＋ `legend`）。
 *
 * 選択肢が個々の `<label>` を持ち、群の名前は `legend`（＝そのフィールドのラベル）が持つ。
 * `name` は {@link uniqueId} で作る — 同じ定義のフォームが 1 画面に 2 つ載っても
 * 選択が混ざらないようにするため。誤りの印は群の**すべての選択肢**へ付ける。
 */
function appendRadioGroup(
  doc: Document,
  form: HTMLFormElement,
  field: FormField,
  translate: Translate,
): FieldParts | null {
  const options = field.options ?? [];
  if (options.length === 0) return null; // 選択肢の無いラジオは描けない（サーバが定義段で弾く）

  const group = doc.createElement('fieldset');
  group.className = `${cls('overlay-form-field')} ${cls('overlay-form-field--radio')}`;
  const legend = doc.createElement('legend');
  fillCaption(doc, legend, field, translate);
  group.appendChild(legend);

  const name = uniqueId(`overlay-form-${field.name}`);
  const radios: HTMLInputElement[] = [];
  for (const option of options) {
    const choice = doc.createElement('label');
    choice.className = cls('overlay-form-choice');
    const radio = doc.createElement('input');
    radio.type = 'radio';
    radio.name = name;
    radio.value = option;
    if (field.required === true) radio.required = true;
    choice.appendChild(radio);
    choice.appendChild(doc.createTextNode(option));
    group.appendChild(choice);
    radios.push(radio);
  }

  const error = createError(doc);
  group.appendChild(error);
  form.appendChild(group);

  const first = radios[0] as HTMLInputElement;
  return {
    field,
    control: first,
    marks: radios,
    error,
    value: () => radios.find((radio) => radio.checked)?.value ?? '',
  };
}

/** フォームを送信し、結果に応じて完了表示・ゲート解錠・エラー表示を行う。 */
async function send(
  context: OverlayContext,
  item: OverlayItem,
  formId: number,
  definition: FormDefinition,
  parts: readonly FieldParts[],
  form: HTMLFormElement,
  submit: HTMLButtonElement,
  status: HTMLElement,
): Promise<void> {
  const values: Record<string, string | boolean> = {};
  for (const part of parts) {
    values[part.field.name] = part.value();
  }

  submit.disabled = true;
  status.hidden = false;
  status.textContent = context.translate('overlay.form.sending');

  try {
    const response = await fetch(`${context.apiBase}/forms/${formId}/submissions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ book: context.bookSlug, page: item.page, values }),
    });
    if (!response.ok) {
      throw new Error(`status ${response.status}`);
    }
    const body = (await response.json()) as { unlocked?: boolean };
    form.replaceChildren();
    status.hidden = false;
    status.textContent = definition.successMessage ?? context.translate('overlay.form.done');
    form.appendChild(status);
    if (body.unlocked === true) {
      // 解錠の反映（読み込み直し）は**利用者が押したときだけ**行う。時間で走らせると
      // 止める手段の無い制限時間になり、読み上げの途中でも画面が入れ替わる（WCAG 2.2.1）。
      const apply = context.unlocked();
      const button = context.doc.createElement('button');
      button.type = 'button';
      button.className = `${cls('button')} ${cls('overlay-form-submit')}`;
      button.textContent = context.translate('overlay.form.unlockApply');
      button.addEventListener('click', () => apply());
      form.appendChild(button);
      // 送信ボタンは消えているので、焦点の行き先を作る（body へ落とさない）。
      button.focus();
    }
  } catch {
    submit.disabled = false;
    status.textContent = context.translate('overlay.form.error');
  }
}
